import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";
import { decodeTime } from "ulid";

import DocumentationButton from "components/DocumentationButton";
import { EmptystateWebhooksColored } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import PermissionGateButton from "components/PermissionGate/Button";
import { WebhooksTierInfo } from "components/TierInfo/WebhooksTierInfo";
import Box from "ds/components/Box";
import { useActiveDrawerId } from "ds/components/DrawerNew/useActiveDrawerId";
import EmptyState from "ds/components/EmptyState";
import { useInitialSort } from "ds/components/Table/hooks/useInitialSort";
import TableSimple from "ds/components/TableSimple";
import useAnalytics from "hooks/useAnalytics";
import useTypedContext from "hooks/useTypedContext";
import { getDocsUrl } from "utils/getDocsUrl";

import { ModuleSettingsContext } from "../Context";
import ModuleSettingsIntegrationsPageLayout from "./components/PageLayout";
import { showWebhookDrawerForm } from "./components/WebhookFormDrawer";
import { GET_MODULE_WEBHOOKS } from "./gql";
import { MODULE_WEBHOOKS_TABLE_CELLS } from "./Table/Cells";
import {
  INITIAL_SORT_DIRECTION,
  INITIAL_SORT_OPTION,
  MODULE_WEBHOOKS_TABLE_FILTERS_STORAGE_KEY,
  SORT_CONFIG,
} from "./Table/constants";
import { useSortingModuleWebhooks } from "./Table/useSortingModuleWebhooks";
import useModuleWebhooksTableStore from "./Table/useTableStore";
import { GetModuleWebhooksGql } from "./types";

const VIRTUALIZATION_THRESHOLD = 50;

const ModuleSettingsIntegrationsWebhooks = () => {
  const { module, canManageModule } = useTypedContext(ModuleSettingsContext);
  const trackAnalytics = useAnalytics();
  const activeRowId = useActiveDrawerId();

  useInitialSort(INITIAL_SORT_OPTION, INITIAL_SORT_DIRECTION);

  const { loading, data } = useQuery<GetModuleWebhooksGql>(GET_MODULE_WEBHOOKS, {
    variables: {
      moduleId: module.id,
    },
    fetchPolicy: "no-cache",
  });

  const webhooks = useMemo(() => {
    const items = data?.module?.integrations?.webhooks || [];

    return items.map((item) => ({
      ...item,
      createdAt: decodeTime(item.id) / 1000,
    }));
  }, [data?.module?.integrations?.webhooks]);

  const sortedWebhooks = useSortingModuleWebhooks(webhooks);

  const handleOnCreateClick = () => {
    showWebhookDrawerForm({
      moduleId: module.id,
    });
  };

  return (
    <>
      <ModuleSettingsIntegrationsPageLayout
        actions={
          <PermissionGateButton
            access={canManageModule}
            variant="primary"
            onPress={handleOnCreateClick}
            disabled={loading}
          >
            Create webhook
          </PermissionGateButton>
        }
      >
        <WebhooksTierInfo />

        {loading && !data && <PageLoading />}

        {webhooks.length > 0 && (
          <TableSimple
            data={sortedWebhooks}
            store={useModuleWebhooksTableStore}
            ariaLabel="Webhooks"
            trackAnalytics={trackAnalytics}
            cellPerColumn={MODULE_WEBHOOKS_TABLE_CELLS}
            filtersStorageKey={MODULE_WEBHOOKS_TABLE_FILTERS_STORAGE_KEY}
            sortConfig={SORT_CONFIG}
            hasPagination={false}
            virtualize={sortedWebhooks.length >= VIRTUALIZATION_THRESHOLD}
            activeRowId={activeRowId}
          />
        )}

        {webhooks.length === 0 && !loading && (
          <Box align="center" justify="center" grow="1" fullWidth>
            <EmptyState
              padding="large"
              icon={EmptystateWebhooksColored}
              title="You do not have any webhooks yet"
              caption="Set up a webhook for your current module to send events to a http endpoint of your choice."
            >
              <Box gap="medium">
                <PermissionGateButton
                  access={canManageModule}
                  variant="primary"
                  onPress={handleOnCreateClick}
                  disabled={loading}
                >
                  Create webhook
                </PermissionGateButton>
                <DocumentationButton
                  to={getDocsUrl("/integrations/webhooks")}
                  label="Documentation"
                />
              </Box>
            </EmptyState>
          </Box>
        )}
      </ModuleSettingsIntegrationsPageLayout>
    </>
  );
};

export default ModuleSettingsIntegrationsWebhooks;
